import {
  ATS_POSITION_VALUES,
  Button,
  Checkbox,
  type ContractTypeFilter,
  EmploymentType,
  EMPLOYMENT_TYPES,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cibli/shared';
import { ChevronDown, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useFilteredJobsContext } from '../contexts/FilteredJobsContext';

const EMPLOYMENT_TYPE_OPTIONS: EmploymentType[] = Object.values(
  EMPLOYMENT_TYPES ?? { FULL_TIME: 'full-time', PART_TIME: 'part-time' },
) as EmploymentType[];

export default function JobFilters() {
  const { t } = useTranslation();
  const { filters, setSearch, setContractTypes, setEmploymentTypes, clearFilters, pagination } =
    useFilteredJobsContext();

  const handleContractTypeToggle = (type: ContractTypeFilter) => {
    if (type === 'ALL') {
      setContractTypes([]);
      return;
    }
    const newTypes = filters.contractTypes.includes(type)
      ? filters.contractTypes.filter(x => x !== type)
      : [...filters.contractTypes, type];
    setContractTypes(newTypes);
  };

  const hasAllContractTypesSelected =
    filters.contractTypes.length === 0 || filters.contractTypes.includes('ALL');

  const handleEmploymentTypeToggle = (type: EmploymentType) => {
    const newTypes = filters.employmentTypes.includes(type)
      ? filters.employmentTypes.filter(t => t !== type)
      : [...filters.employmentTypes, type];
    setEmploymentTypes(newTypes);
  };

  return (
    <div className="space-y-4 mb-6">
      {/* Single row: Search and Advanced filters */}
      <div className="flex flex-col md:flex-row items-center gap-3">
        {/* Search Input */}
        <div className="relative flex-1 w-full md:w-auto">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <Input
            type="text"
            value={filters.search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('dashboard.jobs.filters.searchPlaceholder')}
            aria-label={t('dashboard.jobs.filters.searchPlaceholder')}
            className="pl-10 bg-white focus-visible:ring-0 focus-visible:border-input"
          />
        </div>

        <div className="flex flex-row items-center gap-3">
          {/* Contract Type Multi-Select */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-fit bg-white shadow-sm hover:bg-cj-green-light/30 hover:text-cj-green-dark"
              >
                {t('dashboard.jobs.filters.contractType')}
                {filters.contractTypes.length > 0 && ` (${filters.contractTypes.length})`}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t('dashboard.jobs.filters.contractType')}
                </Label>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="contract-ALL"
                    checked={hasAllContractTypesSelected}
                    onCheckedChange={() => handleContractTypeToggle('ALL')}
                  />
                  <label
                    htmlFor="contract-ALL"
                    className="text-sm cursor-pointer leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {t('dashboard.jobs.filters.contractTypeAll')}
                  </label>
                </div>
                {ATS_POSITION_VALUES.map(type => (
                  <div key={type} className="flex items-center space-x-2">
                    <Checkbox
                      id={`contract-${type}`}
                      checked={filters.contractTypes.includes(type)}
                      onCheckedChange={() => handleContractTypeToggle(type)}
                    />
                    <label
                      htmlFor={`contract-${type}`}
                      className="text-sm cursor-pointer leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {t(`MatchingJobs.contractType.${type}`)}
                    </label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {/* Employment Type Multi-Select */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-fit bg-white shadow-sm hover:bg-cj-green-light/30 hover:text-cj-green-dark"
              >
                {t('dashboard.jobs.filters.employmentTypes')}
                {filters.employmentTypes.length > 0 && ` (${filters.employmentTypes.length})`}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t('dashboard.jobs.filters.employmentTypes')}
                </Label>
                {EMPLOYMENT_TYPE_OPTIONS.map(type => (
                  <div key={type} className="flex items-center space-x-2">
                    <Checkbox
                      id={`employment-${type}`}
                      checked={filters.employmentTypes.includes(type)}
                      onCheckedChange={() => handleEmploymentTypeToggle(type)}
                    />
                    <label
                      htmlFor={`employment-${type}`}
                      className="text-sm cursor-pointer leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {t(`dashboard.jobs.filters.employmentTypeOptions.${type}`)}
                    </label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Clear Filters Button */}
        {pagination.hasActiveFilters && (
          <Button
            onClick={clearFilters}
            variant="outline"
            size="default"
            className="bg-white shadow-sm hover:bg-cj-green-light/30 hover:text-cj-green-dark"
          >
            <X className="h-4 w-4 mr-2" />
            {t('dashboard.jobs.filters.clearFilters')}
          </Button>
        )}
      </div>
    </div>
  );
}
